/* eslint-disable no-case-declarations */
 
import { isEqual } from "lodash";
import { setImmutable, parsePath, getByPath } from "../../utils/setImmutable";
import { Payload } from ".";
import { AppDispatch } from "../../configureStore";
import { FormFieldValue } from "../../interface/formTypes";
import { Loan } from "../../interface/loan";
import {
    BackupExtractJson,
    ExtractReturn,
    ExtractTask,
    FormExtractions,
    IdentifyResult,
    IdentifyReturn,
    IdentifyTask,
    LoanReturn,
    MatchFormsData,
    ReturnActive,
    ReturnFile,
    ReturnReviews,
    ReturnTask,
    SortReturnBody,
    Task,
    TaxReturnPage,
    UploadedReturn,
} from "../../interface/returns";
import { GroupReturn } from "../../interface/returns";
import { FieldType, formTypeDefinitions, getFormFieldType } from "../../utils/generatedFormTypes";
import execute from "../http/execute";
import failure from "../http/failure";
import { get, getBlob, patch, put } from "../http/request";
import success from "../http/success";

 
export type ReturnState = {
    returnReviews: ReturnReviews[];
    activeReturn?: LoanReturn;
    activeReturnTask?: ReturnActive;
    activePdf?: string;
    backupJson: BackupExtractJson;
    extractedJson?: FormExtractions;
    draftExtractedJson?: FormExtractions;
    identificationJson?: Array<TaxReturnPage>;
    pdfs: UploadedReturn[];
    pdf: { [index: string]: UploadedReturn };
    groupTasks: Task[];
    identifyTasks: Task[];
    extractTasks: Task[];
    loan?: Loan;
    activeBox?: number;
    pdfPages: {
        active: number;
        count: number;
    };
    hasChanges: boolean;
    savedExtractedJson?: FormExtractions;
    dirtyFieldPaths: Record<string, true>;
    invalidFieldPaths: Record<string, InvalidFieldInfo>;
    returnTab: number;
    approvedMatchValues: Record<string, string[]>;
};

export type InvalidFieldInfo = {
    formType: string;
    formName: string;
    pageNumber: number;
};

const initialState: ReturnState = {
    returnReviews: [],
    activeReturnTask: undefined,
    activePdf: undefined,
    backupJson: {},
    groupTasks: [],
    identifyTasks: [],
    extractTasks: [],
    pdfs: [],
    pdfPages: {
        active: 0,
        count: 0,
    },
    pdf: {},
    hasChanges: false,
    savedExtractedJson: undefined,
    dirtyFieldPaths: {},
    invalidFieldPaths: {},
    returnTab: 0,
    approvedMatchValues: {},
};

export const RESET_RETURNS = "returns/RESET_RETURNS";

export const RESET_UPDATED_EXTRACTION = "returns/RESET_UPDATED_EXTRACTION";
export const SAVE_UPDATED_EXTRACTION = "returns/SAVE_UPDATED_EXTRACTION";

export const RESTORE_EXTRACTED_JSON = "returns/RESTORE_EXTRACTED_JSON";
export const UPDATE_EXTRACTED_FIELD_VALUE = "returns/UPDATE_EXTRACTED_FIELD_VALUE";
export const BATCH_UPDATE_EXTRACTED_FIELD_VALUES = "returns/BATCH_UPDATE_EXTRACTED_FIELD_VALUES";
export const UPDATE_FORM_EXTRACTED_FIELD_VALUE = "returns/UPDATE_FORM_EXTRACTED_FIELD_VALUE";

export const SAVE_EXTRACTION_REQUEST = "returns/SAVE_EXTRACTION_REQUEST";
export const SAVE_EXTRACTION_SUCCESS = "returns/SAVE_EXTRACTION_SUCCESS";
export const SAVE_EXTRACTION_FAILURE = "returns/SAVE_EXTRACTION_FAILURE";

export const UPDATE_IDENTIFICATION_JSON = "returns/UPDATE_IDENTIFICATION_JSON";
export const SAVE_IDENTIFICATION_REQUEST = "returns/SAVE_IDENTIFICATION_REQUEST";
export const SAVE_IDENTIFICATION_SUCCESS = "returns/SAVE_IDENTIFICATION_SUCCESS";
export const SAVE_IDENTIFICATION_FAILURE = "returns/SAVE_IDENTIFICATION_FAILURE";

export const SAVE_MATCH_FORMS_REQUEST = "returns/SAVE_MATCH_FORMS_REQUEST";
export const SAVE_MATCH_FORMS_SUCCESS = "returns/SAVE_MATCH_FORMS_SUCCESS";
export const SAVE_MATCH_FORMS_FAILURE = "returns/SAVE_MATCH_FORMS_FAILURE";

export const KICKBACK_TASK_REQUEST = "returns/KICKBACK_TASK_REQUEST";
export const KICKBACK_TASK_SUCCESS = "returns/KICKBACK_TASK_SUCCESS";
export const KICKBACK_TASK_FAILURE = "returns/KICKBACK_TASK_FAILURE";

export const SET_RETURN_TAB = "returns/SET_RETURN_TAB";
export const SET_HAS_CHANGES = "returns/SET_HAS_CHANGES";

export const SET_FIELD_INVALID = "returns/SET_FIELD_INVALID";
export const CLEAR_INVALID_FIELDS = "returns/CLEAR_INVALID_FIELDS";
export const SET_INVALID_FIELDS_FOR_FORM = "returns/SET_INVALID_FIELDS_FOR_FORM";

export const ADD_APPROVED_MATCH_VALUE = "returns/ADD_APPROVED_MATCH_VALUE";
export const REMOVE_APPROVED_MATCH_VALUE = "returns/REMOVE_APPROVED_MATCH_VALUE";

export const GET_TAX_RETURN_REQUEST = "returns/GET_TAX_RETURN_REQUEST";
export const GET_TAX_RETURN_SUCCESS = "returns/GET_TAX_RETURN_SUCCESS";
export const GET_TAX_RETURN_FAILURE = "returns/GET_TAX_RETURN_FAILURE";

export const GET_TAX_RETURNS_REQUEST = "returns/GET_TAX_RETURNS_REQUEST";
export const GET_TAX_RETURNS_SUCCESS = "returns/GET_TAX_RETURNS_SUCCESS";
export const GET_TAX_RETURNS_FAILURE = "returns/GET_TAX_RETURNS_FAILURE";

export const GET_RETURN_FILE_REQUEST = "returns/GET_RETURN_FILE_REQUEST";
export const GET_RETURN_FILE_SUCCESS = "returns/GET_RETURN_FILE_SUCCESS";
export const GET_RETURN_FILE_FAILURE = "returns/GET_RETURN_FILE_FAILURE";

export const SAVE_RETURNS_REQUEST = "returns/SAVE_RETURNS_REQUEST";
export const SAVE_RETURNS_SUCCESS = "returns/SAVE_RETURNS_SUCCESS";
export const SAVE_RETURNS_FAILURE = "returns/SAVE_RETURNS_FAILURE";

export const REORDER_RETURN_REQUEST = "returns/REORDER_RETURN_REQUEST";
export const REORDER_RETURN_SUCCESS = "returns/REORDER_RETURN_SUCCESS";
export const REORDER_RETURN_FAILURE = "returns/REORDER_RETURN_FAILURE";

export const COMPLETE_RETURN_REQUEST = "returns/COMPLETE_RETURN_REQUEST";
export const COMPLETE_RETURN_SUCCESS = "returns/COMPLETE_RETURN_SUCCESS";
export const COMPLETE_RETURN_FAILURE = "returns/COMPLETE_RETURN_FAILURE";

export const GET_RETURN_TASK_REQUEST = "returns/GET_RETURN_TASK_REQUEST";
export const GET_RETURN_TASK_SUCCESS = "returns/GET_RETURN_TASK_SUCCESS";
export const GET_RETURN_TASK_FAILURE = "returns/GET_RETURN_TASK_FAILURE";

export const GET_RETURN_PDF_REQUEST = "returns/GET_RETURN_PDF_REQUEST";
export const GET_RETURN_PDF_SUCCESS = "returns/GET_RETURN_PDF_SUCCESS";
export const GET_RETURN_PDF_FAILURE = "returns/GET_RETURN_PDF_FAILURE";

const getUrl = (base64: string): string => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob: Blob = new Blob([byteArray], { type: "application/pdf" });
    const pdfUrl: string = URL.createObjectURL(blob);
    return pdfUrl;
};

type ActionParams =
    | { type: typeof RESET_RETURNS }
    | { type: typeof SET_HAS_CHANGES; hasChanges: boolean }
    | { type: typeof SET_RETURN_TAB; tab: number }
    | { type: typeof GET_TAX_RETURN_SUCCESS; data: LoanReturn }
    | { type: typeof GET_TAX_RETURNS_SUCCESS; data: ReturnTask[] }
    | { type: typeof GET_RETURN_TASK_SUCCESS; data: ReturnActive }
    | { type: typeof GET_RETURN_TASK_REQUEST }
    | { type: typeof GET_RETURN_PDF_SUCCESS; pdfUrl: string }
    | { type: typeof COMPLETE_RETURN_SUCCESS; data: ReturnTask[] }
    | {
          type: typeof REORDER_RETURN_SUCCESS;
          data: { success: true; base64: string; file: ReturnFile };
          input: SortReturnBody;
      }
    | {
          type: typeof GET_RETURN_FILE_SUCCESS;
          data: { success: true; base64: string };
          input: { index: number; name: string; loanNumber: string; fiCode: string };
      }
    | { type: typeof UPDATE_EXTRACTED_FIELD_VALUE; value: FieldType; location: string; silent?: boolean }
    | { type: typeof BATCH_UPDATE_EXTRACTED_FIELD_VALUES; updates: Array<{ location: string; value: FieldType }> }
    | { type: typeof UPDATE_FORM_EXTRACTED_FIELD_VALUE; formType: string; value: any }
    | { type: typeof RESTORE_EXTRACTED_JSON; extractedJson: FormExtractions }
    | { type: typeof RESET_UPDATED_EXTRACTION }
    | { type: typeof SAVE_EXTRACTION_SUCCESS; data: { extractedJson: FormExtractions } }
    | { type: typeof UPDATE_IDENTIFICATION_JSON; identificationJson: IdentifyResult[] }
    | { type: typeof SAVE_IDENTIFICATION_SUCCESS; data: { identificationJson: IdentifyResult[] } }
    | { type: typeof ADD_APPROVED_MATCH_VALUE; matchType: string; value: string }
    | { type: typeof REMOVE_APPROVED_MATCH_VALUE; matchType: string; value: string }
    | { type: typeof SET_FIELD_INVALID; fieldPath: string; invalid: boolean; info?: InvalidFieldInfo }
    | { type: typeof CLEAR_INVALID_FIELDS }
    | {
          type: typeof SET_INVALID_FIELDS_FOR_FORM;
          formType: string;
          entries: { key: string; info: InvalidFieldInfo }[];
      };

function reducer(state = initialState, action: ActionParams): typeof initialState {
    switch (action.type) {
        case RESET_RETURNS:
            return {
                ...state,
                activeReturn: undefined,
                activePdf: undefined,
                pdf: {},
                returnTab: 0,
                loan: undefined,
                approvedMatchValues: {},
            };

        case GET_RETURN_TASK_REQUEST:
            return {
                ...state,
                activePdf: undefined,
            };

        case GET_RETURN_PDF_SUCCESS:
            return {
                ...state,
                activePdf: action.pdfUrl,
            };

        case REORDER_RETURN_SUCCESS:
            const newFiles = (state.activeReturn?.files ?? []).map((file, idx) =>
                idx === action.input.index ? action.data.file : file,
            );

            const needsSorting = newFiles.findIndex(({ sorted }) => !sorted);

            return {
                ...state,
                pdf: {
                    ...state.pdf,
                    [action.input.index.toString()]: {
                        url: getUrl(action.data.base64),
                        name: action.data.file.name,
                    },
                },
                activeReturn: state.activeReturn
                    ? {
                          ...state.activeReturn,
                          files: newFiles,
                      }
                    : undefined,
                returnTab: needsSorting,
            };

        case SET_RETURN_TAB:
            return {
                ...state,
                returnTab: action.tab,
            };

        case SET_HAS_CHANGES:
            return {
                ...state,
                hasChanges: action.hasChanges,
            };

        case COMPLETE_RETURN_SUCCESS:
        case GET_TAX_RETURNS_SUCCESS:
            const identifyTasksList: Task[] = [];
            const extractTasksList: Task[] = [];
            const groupTasksList: Task[] = [];

            action.data.forEach((item) => {
                if (item.type === "identify") {
                    const identifyTask = item.task as IdentifyReturn;
                    identifyTasksList.push({
                        id: `${identifyTask.message.FICode}_${identifyTask.message.loanNumber}`,
                        title: `${identifyTask.message.institutionCode} - Loan ${identifyTask.message.loanNumber}`,
                        status: identifyTask.inUse ? "in_use" : "pending",
                        priority: identifyTask.priority,
                        assignee: identifyTask.user,
                        key: item.key,
                        fileName: identifyTask.message.identity?.fileName,
                    });
                } else if (item.type === "extract") {
                    const extractTask = item.task as ExtractReturn;
                    extractTasksList.push({
                        id: `${extractTask.message.FICode}_${extractTask.message.loanNumber}`,
                        title: `${extractTask.message.institutionCode} - Loan ${extractTask.message.loanNumber}`,
                        status: extractTask.inUse
                            ? "in_use"
                            : extractTask.formErrors.length > 0
                              ? "in_progress"
                              : "pending",
                        priority: extractTask.priority,
                        assignee: extractTask.user,
                        key: item.key,
                        fileName: extractTask.message.identity?.fileName,
                    });
                } else if (item.type === "group") {
                    const groupTask = item.task as GroupReturn;
                    groupTasksList.push({
                        id: `${groupTask.message.FICode}_${groupTask.message.loanNumber}`,
                        title: `${groupTask.message.institutionCode} - Loan ${groupTask.message.loanNumber}`,
                        status: groupTask.inUse ? "in_use" : "pending",
                        priority: groupTask.priority,
                        assignee: groupTask.user,
                        key: item.key,
                        fileName: groupTask.message.identity?.fileName,
                    });
                }
            });

            return {
                ...state,
                identifyTasks: identifyTasksList,
                extractTasks: extractTasksList,
                groupTasks: groupTasksList,
            };

        case GET_RETURN_TASK_SUCCESS:
            let backupJson: BackupExtractJson = {};
            if (action.data.type === "extract") {
                backupJson = ((action.data as unknown as ExtractTask).blobs?.json ?? []).reduce(
                    (acc: BackupExtractJson, curr) => {
                        if (curr.form?.type && curr.isOnList) {
                            const key: keyof BackupExtractJson = curr.form.type
                                .toLowerCase()
                                .replace(/\s/g, "_")
                                .replace(/\(/g, "")
                                .replace(/\)/g, "") as keyof BackupExtractJson;
                            const toAdd = acc[key] ?? [];
                            toAdd.push(curr);
                            return {
                                ...acc,
                                [key]: toAdd,
                            };
                        }
                        return acc;
                    },
                    {},
                );
            }

            return {
                ...state,
                activeReturnTask: action.data,
                backupJson,
                extractedJson:
                    action.data.type === "extract" && (action.data as unknown as ExtractTask).blobs
                        ? (action.data as unknown as ExtractTask).blobs.extractedJson
                        : undefined,
                draftExtractedJson:
                    action.data.type === "extract" && (action.data as unknown as ExtractTask).blobs
                        ? ((action.data as unknown as ExtractTask).blobs.draftExtractedJson ?? undefined)
                        : undefined,
                identificationJson:
                    action.data.type === "identify" && (action.data as unknown as IdentifyTask).blobs?.json
                        ? (action.data as unknown as IdentifyTask).blobs.json
                        : [],
                approvedMatchValues: {},
                hasChanges: false,
                savedExtractedJson:
                    action.data.type === "extract" && (action.data as unknown as ExtractTask).blobs
                        ? (action.data as unknown as ExtractTask).blobs.extractedJson
                        : undefined,
                dirtyFieldPaths: {},
                invalidFieldPaths: {},
            };

        case GET_TAX_RETURN_SUCCESS:
            const foundIndex = action.data.files.findIndex(({ sorted }) => !sorted);
            return {
                ...state,
                activeReturn: action.data,
                returnTab: foundIndex === -1 ? 0 : foundIndex,
            };

        case GET_RETURN_FILE_SUCCESS:
            return {
                ...state,
                pdf: {
                    ...state.pdf,
                    [action.input.index.toString()]: {
                        url: getUrl(action.data.base64),
                        name: action.input.name,
                    },
                },
            };

        // Uses setImmutable instead of cloneDeep + lodash.set.
        // cloneDeep created new references for every object in the tree on every field edit,
        // causing all useSelector consumers to re-render even when their data didn't change.
        // setImmutable only creates new references along the changed path (structural sharing),
        // so sibling form types keep their original references and skip unnecessary re-renders.
        // This eliminates the need for variable and function memoization in consumers
        case UPDATE_EXTRACTED_FIELD_VALUE: {
            const { location, value, silent } = action;
            const path = parsePath(location);
            const newExtractedJson = setImmutable(state.extractedJson || {}, path, value) as FormExtractions;

            if (silent) {
                // Stub creation — update baseline so it doesn't count as a change
                return {
                    ...state,
                    extractedJson: newExtractedJson,
                    savedExtractedJson: setImmutable(state.savedExtractedJson || {}, path, value) as FormExtractions,
                };
            }

            const savedValue = getByPath(state.savedExtractedJson, path);
            const dirtyFieldPaths = { ...state.dirtyFieldPaths };
            if (isEqual(value, savedValue)) {
                delete dirtyFieldPaths[location];
            } else {
                dirtyFieldPaths[location] = true;
            }

            return {
                ...state,
                extractedJson: newExtractedJson,
                dirtyFieldPaths,
                hasChanges: Object.keys(dirtyFieldPaths).length > 0,
            };
        }

        case BATCH_UPDATE_EXTRACTED_FIELD_VALUES: {
            let updatedJson: any = state.extractedJson || {};
            const batchDirtyPaths = { ...state.dirtyFieldPaths };
            action.updates.forEach(({ location: updateLocation, value: updateValue }) => {
                updatedJson = setImmutable(updatedJson, parsePath(updateLocation), updateValue);
                const savedValue = getByPath(state.savedExtractedJson, parsePath(updateLocation));
                if (isEqual(updateValue, savedValue)) {
                    delete batchDirtyPaths[updateLocation];
                } else {
                    batchDirtyPaths[updateLocation] = true;
                }
            });
            return {
                ...state,
                extractedJson: updatedJson as FormExtractions,
                dirtyFieldPaths: batchDirtyPaths,
                hasChanges: Object.keys(batchDirtyPaths).length > 0,
            };
        }

        case UPDATE_FORM_EXTRACTED_FIELD_VALUE: {
            // Only clone the specific form being updated for better performance
            const { formType, value: formValue } = action;

            return {
                ...state,
                extractedJson: {
                    ...state.extractedJson,
                    [formType]: formValue,
                },
                hasChanges: true,
            };
        }

        case RESTORE_EXTRACTED_JSON: {
            return {
                ...state,
                extractedJson: action.extractedJson,
                hasChanges: true,
            };
        }

        case RESET_UPDATED_EXTRACTION: {
            const resetJson = (state.activeReturnTask as unknown as ExtractTask).blobs.extractedJson;
            return {
                ...state,
                extractedJson: resetJson,
                savedExtractedJson: resetJson,
                dirtyFieldPaths: {},
                invalidFieldPaths: {},
                hasChanges: false,
            };
        }

        case SAVE_EXTRACTION_SUCCESS: {
            if (state.activeReturnTask?.type !== "extract") return state;

            const newTask: ExtractTask = {
                ...state.activeReturnTask,
                type: "extract",
                blobs: {
                    ...(state.activeReturnTask as unknown as ExtractTask).blobs,
                    extractedJson: action.data.extractedJson,
                    draftExtractedJson: undefined,
                    loan: (state.activeReturnTask as unknown as ExtractTask).blobs.loan,
                },
            };

            return {
                ...state,
                activeReturnTask: newTask,
                extractedJson: action.data.extractedJson,
                savedExtractedJson: action.data.extractedJson,
                dirtyFieldPaths: {},
                invalidFieldPaths: {},
                hasChanges: false,
                draftExtractedJson: undefined,
            };
        }

        case UPDATE_IDENTIFICATION_JSON: {
            return {
                ...state,
                identificationJson: action.identificationJson,
                hasChanges: true,
            };
        }

        case SAVE_IDENTIFICATION_SUCCESS: {
            if (state.activeReturnTask?.type !== "identify") return state;

            const updatedTask: any = {
                ...state.activeReturnTask,
                blobs: {
                    ...(state.activeReturnTask as any).blobs,
                    json: action.data.identificationJson,
                    loan: (state.activeReturnTask as unknown as IdentifyTask).blobs.loan,
                },
            };

            return {
                ...state,
                activeReturnTask: updatedTask,
                identificationJson: action.data.identificationJson,
                hasChanges: false,
            };
        }

        case ADD_APPROVED_MATCH_VALUE: {
            const existing = state.approvedMatchValues[action.matchType] ?? [];
            if (existing.includes(action.value)) return state;
            return {
                ...state,
                approvedMatchValues: {
                    ...state.approvedMatchValues,
                    [action.matchType]: [...existing, action.value],
                },
            };
        }

        case REMOVE_APPROVED_MATCH_VALUE: {
            const currentValues = state.approvedMatchValues[action.matchType] ?? [];
            return {
                ...state,
                approvedMatchValues: {
                    ...state.approvedMatchValues,
                    [action.matchType]: currentValues.filter((v) => v !== action.value),
                },
            };
        }

        case SET_FIELD_INVALID: {
            const { fieldPath, invalid, info } = action;
            const existing = state.invalidFieldPaths[fieldPath];
            if (invalid) {
                if (!info) return state;
                if (existing && existing.formName === info.formName && existing.pageNumber === info.pageNumber) {
                    return state;
                }
                return {
                    ...state,
                    invalidFieldPaths: { ...state.invalidFieldPaths, [fieldPath]: info },
                };
            }
            if (!existing) return state;
            const next = { ...state.invalidFieldPaths };
            delete next[fieldPath];
            return { ...state, invalidFieldPaths: next };
        }

        case CLEAR_INVALID_FIELDS:
            return { ...state, invalidFieldPaths: {} };

        case SET_INVALID_FIELDS_FOR_FORM: {
            const { formType, entries } = action;
            // Drop any existing entries that belong to this formType, then add the fresh ones.
            const next: Record<string, InvalidFieldInfo> = {};
            for (const [key, info] of Object.entries(state.invalidFieldPaths)) {
                if (info.formType !== formType) next[key] = info;
            }
            let changed = Object.keys(next).length !== Object.keys(state.invalidFieldPaths).length;
            for (const { key, info } of entries) {
                const existing = state.invalidFieldPaths[key];
                if (
                    !existing ||
                    existing.formType !== info.formType ||
                    existing.formName !== info.formName ||
                    existing.pageNumber !== info.pageNumber
                ) {
                    changed = true;
                }
                next[key] = info;
            }
            return changed ? { ...state, invalidFieldPaths: next } : state;
        }

        default:
            return state;
    }
}

export const getReturns = (payload: Payload<{ loanNumber: string; fiCode: string }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_TAX_RETURNS_REQUEST,
        payload,
    });

    execute(get, `/returns`, payload.input)
        .then(success(dispatch, GET_TAX_RETURNS_SUCCESS, payload))
        .catch(failure(dispatch, GET_TAX_RETURNS_FAILURE, payload));
};

export const getReturn = (payload: Payload<{ loanId: string }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_TAX_RETURN_REQUEST,
        payload,
    });

    execute(get, `/return/${payload.input?.loanId}`, {})
        .then(success(dispatch, GET_TAX_RETURN_SUCCESS, payload))
        .catch(failure(dispatch, GET_TAX_RETURN_FAILURE, payload));
};

export const getReturnPdf =
    (payload: Payload<{ index: number; name: string; loanNumber: string; fiCode: string }>) =>
    (dispatch: AppDispatch) => {
        dispatch({
            type: GET_RETURN_FILE_REQUEST,
            payload,
        });

        execute(get, "/return-file", payload.input)
            .then(success(dispatch, GET_RETURN_FILE_SUCCESS, payload))
            .catch(failure(dispatch, GET_RETURN_FILE_FAILURE, payload));
    };

export const reorderReturn = (payload: Payload<SortReturnBody>) => (dispatch: AppDispatch) => {
    dispatch({
        type: REORDER_RETURN_REQUEST,
        payload,
    });

    execute(put, `/returns`, payload.input)
        .then(success(dispatch, REORDER_RETURN_SUCCESS, payload))
        .catch(failure(dispatch, REORDER_RETURN_FAILURE, payload));
};

export const markCompleted = (payload: Payload<LoanReturn>) => (dispatch: AppDispatch) => {
    dispatch({
        type: COMPLETE_RETURN_REQUEST,
        payload,
    });

    execute(patch, `/return-completed`, payload.input)
        .then(success(dispatch, COMPLETE_RETURN_SUCCESS, payload))
        .catch(failure(dispatch, COMPLETE_RETURN_FAILURE, payload));
};

export const getReturnTaskByKey = (payload: Payload<{ key: string }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_RETURN_TASK_REQUEST,
        payload,
    });

    execute(get, `/return-task/${payload.input?.key}`, {})
        .then(success(dispatch, GET_RETURN_TASK_SUCCESS, payload))
        .catch(failure(dispatch, GET_RETURN_TASK_FAILURE, payload));
};

export const getReturnPdfByRef = (payload: Payload<{ container: string; path: string }>) => (dispatch: AppDispatch) => {
    dispatch({
        type: GET_RETURN_PDF_REQUEST,
        payload,
    });

    const onDownloadProgress = (progressEvent: any) => {
        if (payload.onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            payload.onProgress(percentCompleted);
        }
    };

    getBlob(`/return-task-pdf/${payload.input?.container}/${payload.input?.path}`, onDownloadProgress)
        .then((response) => {
            const pdfUrl = URL.createObjectURL(response.data);
            dispatch({
                type: GET_RETURN_PDF_SUCCESS,
                pdfUrl,
            });
            if (payload.onSuccess) {
                payload.onSuccess(pdfUrl);
            }
        })
        .catch((error) => {
            dispatch({
                type: GET_RETURN_PDF_FAILURE,
                error,
            });
            if (payload.onError) {
                payload.onError(error);
            }
        });
};

export const saveExtraction =
    (payload: Payload<{ extractedJson: FormExtractions; key: string; draft?: boolean }>) => (dispatch: AppDispatch) => {
        dispatch({
            type: SAVE_EXTRACTION_REQUEST,
            payload,
        });

        execute(put, "/return-extraction", payload.input)
            .then(success(dispatch, SAVE_EXTRACTION_SUCCESS, payload))
            .catch(failure(dispatch, SAVE_EXTRACTION_FAILURE, payload));
    };

export const updateReturnEntry =
    (
        interfaceName: keyof typeof formTypeDefinitions,
        fieldPath: string,
        fullExtractedJsonPath: string,
        value: FormFieldValue,
    ) =>
    (dispatch: AppDispatch) => {
        const expectedType = getFormFieldType<keyof typeof formTypeDefinitions>(interfaceName, fieldPath);
        let newValue = value;
        if (String(value).length > 0) {
            switch (expectedType) {
                case "string":
                    newValue = String(newValue);
                    break;
                case "number":
                    newValue = Number(newValue);
                    break;
                case "boolean":
                    newValue = Boolean(newValue);
                    break;
                case "undefined":
                    newValue = undefined;
                    break;
                case "array":
                case "null":
                case "any":
                case "unknown":
                case "union":
                case "literal":
                case "void":
                case "object":
                default:
                    break;
            }
        } else {
            newValue = null;
        }

        dispatch({ type: UPDATE_EXTRACTED_FIELD_VALUE, value: newValue, location: fullExtractedJsonPath });
    };

export const batchUpdateReturnEntries =
    (
        updates: Array<{
            interfaceName: keyof typeof formTypeDefinitions;
            fieldPath: string;
            fullExtractedJsonPath: string;
            value: FormFieldValue;
        }>,
    ) =>
    (dispatch: AppDispatch) => {
        const processedUpdates = updates.map(({ interfaceName, fieldPath, fullExtractedJsonPath, value }) => {
            const expectedType = getFormFieldType<keyof typeof formTypeDefinitions>(interfaceName, fieldPath);
            let newValue = value;
            if (String(value).length > 0) {
                switch (expectedType) {
                    case "string":
                        newValue = String(newValue);
                        break;
                    case "number":
                        newValue = Number(newValue);
                        break;
                    case "boolean":
                        newValue = Boolean(newValue);
                        break;
                    case "undefined":
                        newValue = undefined;
                        break;
                    case "array":
                    case "null":
                    case "any":
                    case "unknown":
                    case "union":
                    case "literal":
                    case "void":
                    case "object":
                    default:
                        break;
                }
            } else {
                newValue = null;
            }
            return { location: fullExtractedJsonPath, value: newValue };
        });

        dispatch({ type: BATCH_UPDATE_EXTRACTED_FIELD_VALUES, updates: processedUpdates });
    };

export const updateIdentificationJson = (identificationJson: IdentifyResult[]) => (dispatch: AppDispatch) => {
    dispatch({ type: UPDATE_IDENTIFICATION_JSON, identificationJson });
};

export const saveIdentification =
    (
        payload: Payload<{
            identificationJson: IdentifyResult[];
            key: string;
            draft?: boolean;
        }>,
    ) =>
    (dispatch: AppDispatch) => {
        dispatch({
            type: SAVE_IDENTIFICATION_REQUEST,
            payload,
        });

        execute(put, "/return-identification", payload.input)
            .then(success(dispatch, SAVE_IDENTIFICATION_SUCCESS, payload))
            .catch(failure(dispatch, SAVE_IDENTIFICATION_FAILURE, payload));
    };

export const saveMatchForms =
    (
        payload: Payload<{
            matchData: MatchFormsData;
            key: string;
            draft?: boolean;
        }>,
    ) =>
    (dispatch: AppDispatch) => {
        dispatch({
            type: SAVE_MATCH_FORMS_REQUEST,
            payload,
        });

        execute(put, "/return-match-forms", payload.input)
            .then(success(dispatch, SAVE_MATCH_FORMS_SUCCESS, payload))
            .catch(failure(dispatch, SAVE_MATCH_FORMS_FAILURE, payload));
    };

// Kick a manual-review task back to the previous stage's manual queue.
// key prefix decides the path (extract → identify; group → extract); reason is required.
export const kickbackTask =
    (payload: Payload<{ key: string; reason: string }>) =>
    (dispatch: AppDispatch) => {
        dispatch({
            type: KICKBACK_TASK_REQUEST,
            payload,
        });

        execute(put, "/return-kickback", payload.input)
            .then(success(dispatch, KICKBACK_TASK_SUCCESS, payload))
            .catch(failure(dispatch, KICKBACK_TASK_FAILURE, payload));
    };

export const addApprovedMatchValue = (matchType: string, value: string) => ({
    type: ADD_APPROVED_MATCH_VALUE,
    matchType,
    value,
});

export const removeApprovedMatchValue = (matchType: string, value: string) => ({
    type: REMOVE_APPROVED_MATCH_VALUE,
    matchType,
    value,
});

export const setFieldInvalid = (fieldPath: string, invalid: boolean, info?: InvalidFieldInfo) => ({
    type: SET_FIELD_INVALID,
    fieldPath,
    invalid,
    info,
});

export const clearInvalidFields = () => ({
    type: CLEAR_INVALID_FIELDS,
});

export const setInvalidFieldsForForm = (
    formType: string,
    entries: { key: string; info: InvalidFieldInfo }[],
) => ({
    type: SET_INVALID_FIELDS_FOR_FORM,
    formType,
    entries,
});

export default reducer;
